<!-- 歌曲详情页 -->
<script lang="ts" setup>
import type { FullPrompt, FileInfo, MusicMetadata } from '~/types/generate'
import type { HomeGetMusicDetailResponse, ProgressResponse } from '~/types/app'
import { getMusicDetail } from '~/apis/app'
import { useToggleUpvote, useGenerateAction, useEditDetailsModal } from '~/composables/useCommonFunction'
import { useAuthStore } from '~/stores/authStore'
import HomePlay from '~/assets/icons/homePlay.svg?component'
import HomeUpvote from '~/assets/icons/homeUpvote.svg?component'
import HomeUnUpvote from '~/assets/icons/homeUnUpvote.svg?component'
import HomeShare from '~/assets/icons/homeShare.svg?component'
import HomeMore from '~/assets/icons/homeMore.svg?component'
import MoreActionGenerateSimilar from '~/assets/icons/moreActionGenerateSimilar.svg?component'
import MoreActionEditDetails from '~/assets/icons/moreActionEditDetails.svg?component'
import Loading from '~/assets/icons/loading.svg?component'
import PlayingIcon from '~/assets/icons/playingIcon.gif'
import SongActionsMenu from '~/components/SongMoreActionsPopover.vue'
import EditSongDetailsModal from '~/components/EditSongDetailsModal.vue'
import { usePlayerStore } from '~/stores/playerStore'
import { useShareModalStore } from '~/stores/shareModalStore'

const route = useRoute()
const authStore = useAuthStore()
const playerStore = usePlayerStore()
const shareModalStore = useShareModalStore()

const { requireAuth } = useAuthGuard()

// 处理播放按钮点击
const handlePlayClick = async () => {
  if (!song.value) return
  // 这里断言为 MyGeneratedMusicItem，避免类型不兼容（运行时结构已在 parseSongItem 中兼容处理）
  await playerStore.playSong(song.value as unknown as ProgressResponse)
}

// #region 点赞/取消点赞 相关
const { handleToggleUpvote: handleToggleUpvoteBase } = useToggleUpvote<ReturnType<typeof parseSongItem>>((uuid, updates) => {
  if (songData.value) {
    // 创建新对象以确保响应式更新
    songData.value = {
      ...songData.value,
      isVote: updates.isVote,
      upvote_count: updates.upvote_count
    }
  }
})

const handleToggleUpvote = async () => {
  if (!song.value) return
  await handleToggleUpvoteBase(song.value as ReturnType<typeof parseSongItem>)
}
// #endregion

// 分享
const openShare = () => {
  if (!song.value) return
  const title = song.value.metadata?.type === 'upload' && song.value.title === 'my_2024-09-04_01_37_04' && song.value.fullPrompt?.title || song.value.title || song.value.metadata?.prompt || `Untitled - ${titleFormatDate(song.value.createdAt)}`
  shareModalStore.openShare({
    title,
    uuid: song.value.uuid
  })
}

// #region Generate similar 相关
const { handleGenerateAction } = useGenerateAction<ReturnType<typeof parseSongItem>>()

const handleGenerateSimilar = async () => {
  if (!song.value) return
  await handleGenerateAction('generate_similar', song.value)
}
// #endregion

// #region  编辑 相关
const { editModalOpen: editSongDetailsModalOpen, handleEditModalToggle: handleEditSongDetailsToggle } = useEditDetailsModal()

// 处理打开编辑歌曲详情弹窗
const handleOpenEditSongDetails = () => {
  handleEditSongDetailsToggle(true)
}

const handleEditSuccess = (payload: { title: string, lyrics: string }) => {
  if (!songData.value) return
  songData.value = {
    ...songData.value,
    title: payload.title?.trim(),
    metadata:{
      ...songData.value.metadata,
      prompt: payload.lyrics
    }
  }
}

// 处理编辑歌曲详情保存
const handleEditSongDetailsSaved = (payload: { title: string, lyrics: string }) => {
  handleEditSuccess(payload)
}
// #endregion

// 解析数据
function parseSongItem(item: ProgressResponse) {
  let fullPrompt: FullPrompt | null = null
  let fileInfo: FileInfo | null = null
  let metadata: MusicMetadata | null = null

  try {
    const rawFullPrompt = item.fullPrompt as unknown
    if (typeof rawFullPrompt === 'string') {
      fullPrompt = JSON.parse(rawFullPrompt)
    } else if (rawFullPrompt) {
      fullPrompt = rawFullPrompt as FullPrompt
    }
  } catch (e) {
    console.warn('Failed to parse fullPrompt:', e)
  }

  try {
    const rawFileInfo = item.fileInfo as unknown
    if (rawFileInfo && typeof rawFileInfo === 'string') {
      fileInfo = JSON.parse(rawFileInfo)
    } else if (rawFileInfo) {
      fileInfo = rawFileInfo as FileInfo
    }
  } catch (e) {
    console.warn('Failed to parse fileInfo:', e)
  }

  try {
    const rawMetadata = item.metadata as unknown
    if (typeof rawMetadata === 'string') {
      metadata = JSON.parse(rawMetadata)
    } else if (rawMetadata) {
      metadata = rawMetadata as MusicMetadata
    }
  } catch (e) {
    console.warn('Failed to parse metadata:', e)
  }

  return {
    ...item,
    fullPrompt,
    fileInfo,
    metadata
  }
}

const { data: songData, pending: isLoading } = await useAsyncData(() => `song-detail-${route.params.id}`, async () => {
  const uuid = route.params.id as string
  if (!uuid) {
    return null
  }

  try {
    const { data } = await getMusicDetail({ uuids: uuid })
    const response = data as HomeGetMusicDetailResponse
    const clips
      = typeof response.clips === 'string'
        ? decryptData<ProgressResponse[]>(response.clips)
        : response.clips

    if (clips.length > 0) {
      const progressItem = clips[0]
      return parseSongItem(progressItem)
    }

    return null
  } catch (error) {
    console.error('Failed to fetch song detail:', error)
    throw error
  }
})

const song = computed(() => songData.value)
const isFromHome = computed(() => route.query.from === 'home')
const songActionsExcludeIds = computed(() => {
  const limitActions = isFromHome.value || !authStore.isAuthenticated
  if (!limitActions) return []
  return [
    'remix',
    'extend',
    'cover',
    'download',
    'download_mp3',
    'download_wav',
    'download_video',
    'edit_details',
    'publish',
    'move_to_trash',
    'add_to_playlist',
    'add_to_queue',
    'report'
  ]
})

function getDaySuffix(day: number): string {
  if (day > 3 && day < 21) return 'th'
  switch (day % 10) {
    case 1: return 'st'
    case 2: return 'nd'
    case 3: return 'rd'
    default: return 'th'
  }
}

// 格式化日期
function formatDate(dateString: string): string {
  try {
    const date = new Date(dateString)
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
    const month = months[date.getMonth()]
    const day = date.getDate()
    const year = date.getFullYear()
    const hours = date.getHours()
    const minutes = date.getMinutes()
    const ampm = hours >= 12 ? 'Pm' : 'Am'
    const displayHours = hours % 12 || 12
    const displayMinutes = minutes.toString().padStart(2, '0')

    return `${month} ${day}${getDaySuffix(day)}, ${year} ${displayHours}:${displayMinutes} ${ampm}`
  } catch {
    return dateString
  }
}

// 获取创建日期
const createdDate = computed(() => {
  if (!song.value?.createdAt) {
    return ''
  }
  return formatDate(song.value.createdAt)
})

// 歌词文本：优先使用 fullPrompt.lyrics，其次 fullPrompt.prompt，再次 metadata.prompt
const lyricsText = computed(() => {
  const s = song.value as any
  if (!s) return ''
  const metadataPrompt = s.metadata?.prompt || ''
  return metadataPrompt
})

// 模型版本映射
const modelVersionMap = [
  { value: 'v5', label: 'v7' },
  { value: 'v4.5+', label: 'V6' },
  { value: 'v4.5', label: 'v5' },
  { value: 'v4', label: 'v4' }
]

// 根据 value 获取对应的 label
const getModelVersionLabel = (value: string | undefined | null): string => {
  if (!value) return ''
  const normalizedValue = value.toLowerCase()
  const mapped = modelVersionMap.find(item => item.value.toLowerCase() === normalizedValue)
  return mapped?.label || value
}

const titleFormatDate = (value?: string | number | Date | null) => {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const y = date.getFullYear()
  const m = `${date.getMonth() + 1}`.padStart(2, '0')
  const d = `${date.getDate()}`.padStart(2, '0')
  return `${y}-${m}-${d}`
}

// SEO 元信息：基于当前歌曲动态生成标题、描述、链接和封面 URL
const seoSongTitle = computed(() => {
  const s = song.value as any
  if (!s) return ''
  return s.metadata?.type === 'upload' && s.title === 'my_2024-09-04_01_37_04' && s.fullPrompt?.title
    || s.title
    || s.metadata?.prompt
    || `Untitled - ${titleFormatDate(s.createdAt)}`
})

const seoSongId = computed(() => String(route.params.id ?? ''))

const seoCoverUrl = computed(() => {
  const s = song.value as any
  return s?.fileInfo?.cosUrl || ''
})

useSeoMeta({
  title: () => seoSongTitle.value
    ? `${seoSongTitle.value} – XMUSIO AI Music`
    : 'XMUSIO AI Music',
  description: () => seoSongTitle.value
    ? `Listen to '${seoSongTitle.value}', an original AI-generated song created with XMUSIO.`
    : 'Listen to AI-generated songs created with XMUSIO.',
  ogTitle: () => seoSongTitle.value || 'XMUSIO AI Music',
  ogDescription: () => 'An AI-generated song created with XMUSIO.',
  ogType: 'music.song',
  ogUrl: () => `https://www.xmusio.com/library/songs/${seoSongId.value}`,
  // 将封面 URL 写入 robots 字段，占位符替换为真实地址
  robots: () => seoCoverUrl.value || ''
})
</script>

<template>
  <div :class="`flex-1 flex flex-col md:flex-row gap-y-[24px] md:gap-y-0 md:gap-x-[32px] ${playerStore.currentSong ? 'h-[calc(100%-64px)] md:h-[calc(100%-68px)]' : 'h-full'} bg-[linear-gradient(180deg,#64647E_-22.99%,#131318_14.96%)] px-[16px] py-[16px] md:p-[32px] overflow-auto md:overflow-hidden`">
    <!-- 加载状态 -->
    <div
      v-if="isLoading"
      class="flex-1 flex items-center justify-center"
    >
      <Loading class="size-[32px] text-[#E7E7EC] animate-spin" />
    </div>

    <!-- 歌曲详情 -->
    <template v-else-if="song">
      <!-- 左侧面板 -->
      <div class="flex flex-col gap-y-[24px] md:gap-y-[40px] w-full md:w-[250px] md:flex-shrink-0">
        <div class="flex flex-col gap-y-[16px] w-full">
          <!-- 封面图 + 播放/暂停按钮 -->
          <div class="relative w-full aspect-square rounded-[8px] bg-[#5E5E77] group overflow-hidden">
            <img
              :src="song.fileInfo?.cosUrl"
              alt="Song cover"
              :class="`w-full h-full object-cover scale-[1.1] transition-transform duration-200 group-hover:opacity-50 ${playerStore.isPlaying && playerStore.currentSong?.uuid === song.uuid && 'opacity-50'}`"
            >
            <!-- 播放中 -->
            <img
              v-if="playerStore.isPlaying && playerStore.currentSong?.uuid === song.uuid"
              class="absolute top-1/2 left-1/2 translate-[-50%] size-[48px] group-hover:hidden"
              :src="PlayingIcon"
            >
            <!-- 播放 / 暂停按钮 -->
            <div
              class="absolute top-0 left-0 flex justify-center items-center size-full opacity-0 group-hover:opacity-100 transition-opacity duration-200 cursor-pointer"
              @click.stop="handlePlayClick"
            >
              <UIcon
                v-if="!playerStore.isPlaying || playerStore.currentSong?.uuid !== song?.uuid"
                name="weui:play-filled"
                class="size-[48px] text-white drop-shadow-lg"
              />
              <UIcon
                v-else
                name="weui:pause-filled"
                class="size-[48px] text-white drop-shadow-lg"
              />
            </div>
          </div>

          <!-- 移动端：标题 & 日期版本信息 -->
          <div class="md:hidden flex flex-col gap-y-[8px] mt-[16px]">
            <h1 class="text-[22px] leading-[30px] text-white font-semibold line-clamp-2">
              {{ song.metadata?.type === 'upload' && song.title === 'my_2024-09-04_01_37_04' && song.fullPrompt?.title || song.title || song.metadata?.prompt || `Untitled - ${titleFormatDate(song.createdAt)}` }}
            </h1>
            <div class="flex items-center gap-x-[12px] text-[12px]">
              <span class="text-white/90">
                {{ createdDate }}
              </span>
              <span class="text-[12px] text-[#8888A1] bg-transparent border border-[#5E5E77] px-[4px] rounded-[4px]">
                {{ getModelVersionLabel(song.metadata?.model_badges?.songrow?.display_name) }}
              </span>
            </div>
          </div>

          <!-- 交互控件 -->
          <div class="hidden md:flex items-center gap-x-[16px]">
            <div class="flex items-center gap-x-[12px]">
              <!-- 播放 -->
              <div class="flex items-center gap-x-[4px] text-[14px] text-[#8888A1] hover:text-[#E7E7EC] leading-[24px] px-[4px] cursor-pointer">
                <HomePlay class="size-[16px]" />
                <span>{{ song.play_count || 0 }}</span>
              </div>

              <!-- 点赞 -->
              <div
                class="flex items-center gap-x-[4px] text-[14px] text-[#8888A1] hover:text-[#E7E7EC] leading-[24px] px-[4px] cursor-pointer"
                @click="requireAuth(() => handleToggleUpvote())"
              >
                <component
                  :is="song.isVote === 1 ? HomeUpvote : HomeUnUpvote"
                  :class="['size-[16px]', song.isVote === 1 ? 'text-[#F24B4B]' : 'text-[#8888A1]']"
                />
                <span>{{ song.upvote_count || 0 }}</span>
              </div>

              <!-- 分享 -->
              <div
                class="flex items-center text-[#8888A1] hover:text-[#E7E7EC] cursor-pointer"
                @click="requireAuth(() => openShare())"
              >
                <HomeShare class="size-[16px] m-[4px]" />
              </div>
            </div>

            <!-- 更多选项 -->
            <div class="ml-auto hidden md:block">
              <SongActionsMenu
                :song="song"
                :exclude-ids="songActionsExcludeIds"
                @update:is-public="(value) => {
                  if (songData) {
                    songData = {
                      ...songData,
                      is_public: value
                    }
                  }
                }"
                @deleted="() => {
                  // 删除后导航到首页
                  navigateTo('/')
                }"
                @edit-success="handleEditSuccess"
              >
                <div class="flex items-center text-[#B8B8C7] hover:text-[#E7E7EC] cursor-pointer">
                  <HomeMore class="size-[24px]" />
                </div>
              </SongActionsMenu>
            </div>
          </div>

          <!-- 移动端：操作按钮卡片 -->
          <div class="mt-[16px] grid grid-cols-4 gap-[8px] md:hidden">
            <!-- 播放次数 -->
            <button
              class="flex flex-col items-center justify-center py-[10px] rounded-[10px] bg-[#34344A] text-[12px] text-[#E7E7EC] active:bg-[#40405a]"
              type="button"
              @click="handlePlayClick"
            >
              <HomePlay class="size-[18px] mb-[4px]" />
              <span>{{ song.play_count || 0 }}</span>
            </button>

            <!-- 点赞 -->
            <button
              class="flex flex-col items-center justify-center py-[10px] rounded-[10px] bg-[#34344A] text-[12px] text-[#E7E7EC] active:bg-[#40405a]"
              type="button"
              @click="requireAuth(() => handleToggleUpvote())"
            >
              <component
                :is="song.isVote === 1 ? HomeUpvote : HomeUnUpvote"
                :class="['size-[18px] mb-[4px]', song.isVote === 1 ? 'text-[#F24B4B]' : 'text-[#E7E7EC]']"
              />
              <span>{{ song.upvote_count || 0 }}</span>
            </button>

            <!-- 分享 -->
            <button
              class="flex flex-col items-center justify-center py-[10px] rounded-[10px] bg-[#34344A] text-[12px] text-[#E7E7EC] active:bg-[#40405a]"
              type="button"
              @click="requireAuth(() => openShare())"
            >
              <HomeShare class="size-[18px] mb-[4px]" />
              <span>Share</span>
            </button>

            <!-- 更多 -->
            <SongActionsMenu
              :song="song"
              :exclude-ids="songActionsExcludeIds"
              @update:is-public="(value) => {
                if (songData) {
                  songData = {
                    ...songData,
                    is_public: value
                  }
                }
              }"
              @deleted="() => {
                // 删除后导航到首页
                navigateTo('/')
              }"
              @edit-success="handleEditSuccess"
            >
              <button
                class="flex flex-col items-center justify-center w-full py-[10px] rounded-[10px] bg-transparent border border-[#5E5E77] text-[12px] text-[#E7E7EC] active:bg-[#2a2a3a]"
                type="button"
              >
                <HomeMore class="size-[18px] mb-[4px]" />
                <span>More</span>
              </button>
            </SongActionsMenu>
          </div>

          <!-- Generate similar 按钮 -->
          <button
            class="w-full py-[8px] bg-transparent hover:bg-[#1E1E2C] text-[#E7E7EC] rounded-[8px] border border-[#FFFFFF80] transition-colors cursor-pointer flex items-center justify-center gap-x-[8px]"
            @click="requireAuth(() => handleGenerateSimilar())"
          >
            <MoreActionGenerateSimilar class="size-[20px]" />
            <span class="text-[14px] leading-[20px]">Generate similar</span>
          </button>
        </div>

        <!-- Style 部分 -->
        <div class="flex flex-col gap-y-[6px] flex-[1] overflow-hidden mt-[8px] md:mt-0">
          <h3 class="text-[16px] font-bold text-[#E7E7EC]">
            Style
          </h3>
          <div class="flex flex-wrap gap-[8px] h-[calc(100%-30px)] overflow-y-auto">
            <span
              v-if="song.metadata?.tags"
              class="text-[14px] text-[#8888A1] leading-[20px]"
            >
              {{ song.metadata?.tags }}
            </span>
            <span
              v-else
              class="text-[14px] text-[#5E5E77]"
            >
              No style tags
            </span>
          </div>
        </div>
      </div>

      <!-- 右侧面板 -->
      <div class="flex-1 flex flex-col gap-y-[24px] md:gap-y-[32px] min-w-0">
        <div class="hidden md:flex flex-col gap-y-[16px]">
          <!-- 标题 -->
          <h1 class="text-[40px] text-white leading-[50px] line-clamp-1">
            {{ song.metadata?.type === 'upload' && song.title === 'my_2024-09-04_01_37_04' && song.fullPrompt?.title || song.title || song.metadata?.prompt || `Untitled - ${titleFormatDate(song.createdAt)}` }}
          </h1>

          <!-- 日期和版本 -->
          <div class="flex items-center gap-x-[16px]">
            <span class="text-[12px] text-white leading-[16px]">
              {{ createdDate }}
            </span>
            <div class="flex items-center gap-x-[8px]">
              <span class="text-[12px] text-[#8888A1] bg-transparent border border-[#5E5E77] px-[4px] rounded-[4px]">
                {{ getModelVersionLabel(song.metadata?.model_badges?.songrow?.display_name) }}
              </span>
              <button
                v-if="!(isFromHome || !authStore.isAuthenticated)"
                class="flex items-center justify-center size-[24px] text-[#E7E7EC] cursor-pointer hover:text-white transition-colors"
                @click="handleOpenEditSongDetails"
              >
                <MoreActionEditDetails class="size-[16px]" />
              </button>
            </div>
          </div>
        </div>

        <!-- Lyrics 部分 -->
        <div class="flex flex-col gap-y-[16px] flex-1 overflow-hidden">
          <h2 class="text-[20px] font-bold text-white">
            Lyrics
          </h2>
          <div class="flex-1 text-[16px] text-[#E7E7EC] leading-[24px] whitespace-pre-wrap overflow-y-auto">
            <template v-if="lyricsText">
              {{ lyricsText }}
            </template>
            <template v-else>
              <div class="text-[14px] text-[#5E5E77]">
                No lyrics available
              </div>
            </template>
          </div>
        </div>
      </div>
    </template>

    <!-- 未找到歌曲 -->
    <div
      v-else
      class="flex-1 flex items-center justify-center"
    >
      <div class="text-center">
        <p class="text-[18px] text-[#8888A1] mb-[8px]">
          Song not found
        </p>
        <NuxtLink
          to="/"
          class="text-[14px] text-[#BF2AFF] hover:underline"
        >
          Go back to home
        </NuxtLink>
      </div>
    </div>

    <!-- 编辑歌曲详情弹窗 -->
    <EditSongDetailsModal
      :open="editSongDetailsModalOpen"
      :song="song"
      @update:open="(value) => { editSongDetailsModalOpen = value }"
      @saved="handleEditSongDetailsSaved"
    />
  </div>
</template>
